import { Stack } from "@mui/material";
import moment from "moment";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { NetworkRequestState } from "../../../common/models";
import { Api } from "../../../common/service/Api";
import { PrivacyNoticeDto } from "../../../common/service/server/controller/PrivacyNoticeController";
import { CollectionGroupDetailsDto } from "../../../common/service/server/dto/CollectionGroupDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import {
  OrganizationPublicId,
  PrivacyCenterNoticePublicId,
  PrivacyCenterNoticeStatus,
  PrivacyCenterPublicId,
  UUIDString,
} from "../../../common/service/server/Types";
import {
  NoticeEditorMetadataResponse,
  PrivacyCenterDto,
  PrivacyCenterNoticeDto,
  PrivacyCenterNoticeSectionDto,
  PrivacyCenterPolicyDto,
  UpdatePrivacyCenterNoticeRequest,
} from "../../../common/service/server/dto/PrivacyCenterDto";
import { ConfirmDialog } from "../../components/dialog";
import { useDataSubjectTypes, usePrimaryOrganization } from "../../hooks";
import { usePrivacyCenter, usePrivacyPolicy } from "../../hooks/usePrivacyCenter";
import {
  isCaApplicantGroup,
  isCaContractorGroup,
  isCaEmployeeGroup,
  isEeaUkEmployeeGroup,
} from "../../util/consumerGroups";
import {
  CoreSectionTemplates,
  getSectionContentsFromTemplate,
  NewSectionType,
  SectionTemplateTypeToSectionHeader,
} from "../../util/privacyCenterUtil";
import { truncateWithEllipsis } from "../../util/text";
import {
  AddNewSectionDropdown,
  DatePicker,
  ExplanatoryCard,
  Modal,
  SectionEditor,
  useSnackBar,
} from "@truevault/aquilo";
import { SectionCallout } from "../SectionCallout";

import { Form } from "react-final-form";
import { useMutation } from "@tanstack/react-query";
import { Cached as CachedIcon, Done as DoneIcon } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  CreateSectionApiArgs,
  useCreateSectionApi,
  useDeleteSectionApi,
  usePrivacyCenterNotice,
  usePrivacyCenterNotices,
} from "../../hooks/usePrivacyCenterNotice";
import { NewSectionDropdownTemplate } from "@truevault/aquilo/src/components/dropdowns/AddNewSectionDropdown";
import {
  PrivacyCenterPublishDatePickerModal,
  usePrivacyCenterPublish,
} from "./PrivacyCenterPublish";
import {
  DevInstructionsStartWithTruevaultPrivacyPolicyTemplatePromptSlug,
  StartWithTruevaultPrivacyPolicyTemplatePrompt,
} from "../../pages/stay-compliant/StayCompliantPrivacyCenterPage";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../pages/stay-compliant/PrivacyNoticeChecklistItems";
import {
  PrivacyCenterFaviconSettingsItem,
  PrivacyCenterHeaderLogoSettingsItem,
  PrivacyCenterLogoLinkUrlSettingsItem,
  PrivacyCenterTitleSettingsItem,
  PrivacyCenterWelcomeTextSettingsItem,
} from "./PrivacyCenterSettingsItems";

export type UseEditPrivacyCenter = {
  requests: {
    privacyCenter: NetworkRequestState;
    notice: NetworkRequestState;
    allNoticesRequest: NetworkRequestState;
    collectionGroups: NetworkRequestState;
  };
  props: Omit<
    EditPrivacyCenterProps,
    | "hasPublishedNotice"
    | "publishButtonEnabledRef"
    | "showPublishDateModal"
    | "setShowPublishDateModal"
  >; // omit hasPublishedNotice since it depends on different state
  onSave: () => Promise<void>;
};

interface UpdateNoticeArgs {
  organizationId: OrganizationPublicId;
  privacyCenterId: PrivacyCenterPublicId;
  noticeId: PrivacyCenterNoticePublicId;
  updateNoticeBody: UpdatePrivacyCenterNoticeRequest;
}

export function useUpdateNoticeApi() {
  const { mutateAsync: updateNotice, ...rest } = useMutation(
    ({ organizationId, privacyCenterId, noticeId, updateNoticeBody }: UpdateNoticeArgs) =>
      Api.notice.updateNotice(organizationId, privacyCenterId, noticeId, updateNoticeBody),
  );

  return { updateNotice, ...rest };
}

export const useEditPrivacyCenter = (): UseEditPrivacyCenter => {
  const [org] = usePrimaryOrganization();
  const orgId = org.id;

  const [remotePrivacyCenter, privacyCenterRequest, privacyCenterRefresh] = usePrivacyCenter(orgId);
  const [remotePrivacyPolicy, __, privacyPolicyRefresh] = usePrivacyPolicy(
    orgId,
    remotePrivacyCenter?.id,
  );
  const [allNotices, allNoticesRequest, allNoticesRefresh] = usePrivacyCenterNotices(
    orgId,
    remotePrivacyCenter?.id,
  );
  const [collectionGroups, collectionGroupsRequest] = useDataSubjectTypes(orgId);

  const { result: noticeDto, request: noticeRequest } = useDataRequest({
    queryKey: ["notice", "opt-out", orgId],
    api: () => Api.privacyNotice.getOptOutPrivacyNotice(orgId),
  });

  const [saving, setSaving] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  const draftPrivacyPolicyNotice = remotePrivacyCenter?.notices?.find(
    (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "DRAFT",
  );

  let publishedNotices = allNotices
    ?.filter((notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED")
    ?.sort((a, b) => b.version - a.version);
  let latestPublishedNoticeUpdatedOn =
    publishedNotices && publishedNotices.length > 0 ? publishedNotices[0].publishedAt : null;

  const draftPrivacyPolicyNoticeId = draftPrivacyPolicyNotice?.publicId;

  const { updateNotice } = useUpdateNoticeApi();
  const handleSave = async () => {
    try {
      setSaving(true); // If you manage saving/loading state
      const updateNoticeBody = {
        status: "PUBLISHED" as PrivacyCenterNoticeStatus,
        name: draftPrivacyPolicyNotice.name,
        slug: draftPrivacyPolicyNotice.slug,
        sections: draftPrivacyPolicyNotice.sections.map((section) => ({
          ...section, // Ensure each section matches UpdatePrivacyCenterNoticeSectionRequest
        })),
        publishedAt: draftPrivacyPolicyNotice.publishedAt || moment().format("MM-DD-YYYY"),
      };

      // Update the existing published notice
      await updateNotice({
        organizationId: orgId,
        privacyCenterId: draftPrivacyPolicyNotice.privacyCenterId,
        noticeId: draftPrivacyPolicyNoticeId,
        updateNoticeBody: updateNoticeBody,
      });
    } catch (error) {
      console.error("Failed to save the notice:", error);
    } finally {
      setSaving(false);
      setHasSaved(true); // Reset saving state
    }
  };

  const refresh = async () => {
    const prs = [privacyCenterRefresh(), privacyPolicyRefresh()];
    await Promise.all(prs);
  };

  const deleteSectionApi = useCallback(
    async (sectionId) => {
      await Api.privacyCenter.deletePrivacyCenterPolicySection(
        orgId,
        remotePrivacyCenter?.id,
        sectionId,
      );
    },
    [orgId, remotePrivacyCenter?.id],
  );

  const { fetch: deleteSection } = useActionRequest({ api: deleteSectionApi });

  const handleDeleteSection = async (id: UUIDString) => {
    await deleteSection(id);
    await refresh();
  };

  const jobApplicantsGroup = useMemo(
    () => collectionGroups.find((cg) => isCaApplicantGroup(cg)),
    [collectionGroups],
  );
  const employeeGroup = useMemo(
    () => collectionGroups.find((cg) => isCaEmployeeGroup(cg)),
    [collectionGroups],
  );
  const contractorGroup = useMemo(
    () => collectionGroups.find((cg) => isCaContractorGroup(cg)),
    [collectionGroups],
  );
  const eeaUkEmployeeGroup = useMemo(
    () => collectionGroups.find((cg) => isEeaUkEmployeeGroup(cg)),
    [collectionGroups],
  );

  return {
    requests: {
      privacyCenter: privacyCenterRequest,
      notice: noticeRequest,
      allNoticesRequest: allNoticesRequest,
      collectionGroups: collectionGroupsRequest,
    },
    props: {
      org,
      privacyCenter: remotePrivacyCenter,
      privacyCenterRefresh,
      privacyPolicy: remotePrivacyPolicy,
      allNotices,
      allNoticesRefresh,
      draftPrivacyPolicyNoticeId,
      optOut: noticeDto,
      latestPublishedNoticeUpdatedOn,

      jobApplicantsGroup,
      employeeGroup,
      contractorGroup,
      eeaUkEmployeeGroup,
      hasSaved,
      saving,

      onSave: handleSave,
      onDeleteSection: handleDeleteSection,
    },
    onSave: handleSave,
  };
};

type EditPrivacyCenterProps = {
  org: OrganizationDto;
  privacyCenter: PrivacyCenterDto;
  privacyCenterRefresh: () => void;
  privacyPolicy: PrivacyCenterPolicyDto;
  allNotices: PrivacyCenterNoticeDto[];
  allNoticesRefresh: () => void;
  draftPrivacyPolicyNoticeId: PrivacyCenterNoticePublicId;
  optOut: PrivacyNoticeDto;
  latestPublishedNoticeUpdatedOn: string | null; // null if there are no published notices

  jobApplicantsGroup: CollectionGroupDetailsDto | undefined;
  employeeGroup: CollectionGroupDetailsDto | undefined;
  contractorGroup: CollectionGroupDetailsDto | undefined;
  eeaUkEmployeeGroup: CollectionGroupDetailsDto | undefined;

  hasSaved: boolean;
  saving: boolean;

  publishButtonEnabledRef: React.MutableRefObject<boolean>;
  showPublishDateModal: boolean;
  setShowPublishDateModal: React.Dispatch<React.SetStateAction<boolean>>;

  onSave: () => Promise<void>;
  onDeleteSection: (sectionId: string) => void;
};

type ProgressFooterSaveProps = {
  saving: boolean;
  isSaved: boolean;
};

export const ProgressFooterSaveNotification = ({ saving, isSaved }: ProgressFooterSaveProps) => {
  return (
    <>
      <span className="progress-footer-save-icon">
        {saving ? (
          <CachedIcon />
        ) : isSaved ? (
          <DoneIcon />
        ) : (
          <span style={{ visibility: "hidden" }}>
            <CachedIcon />
          </span>
        )}
      </span>
      <span className="progress-footer-save-text">
        {saving ? (
          "Saving"
        ) : isSaved ? (
          "Saved"
        ) : (
          <span style={{ visibility: "hidden" }}>Saving</span>
        )}
      </span>
    </>
  );
};

interface PendingUpdate {
  header?: string;
  content?: string;
  placeholder?: string;
  displayOrder?: number;
  addToNavigation?: boolean;
  locked?: boolean;
  isDeleted?: boolean;
}

type PendingUpdateBySectionId = {
  [sectionId: string]: PendingUpdate;
};

export const EditPrivacyCenter: React.FC<EditPrivacyCenterProps> = ({
  privacyCenter,
  privacyCenterRefresh,
  draftPrivacyPolicyNoticeId,
  onDeleteSection,
  org,
  latestPublishedNoticeUpdatedOn,
  allNoticesRefresh,
  publishButtonEnabledRef,
  showPublishDateModal,
  setShowPublishDateModal,
}) => {
  const [draftNotice, , refreshDraftNotice] = usePrivacyCenterNotice(
    org.id,
    privacyCenter.id,
    draftPrivacyPolicyNoticeId,
  );

  const [confirmDeleteId, setConfirmDeleteId] = useState<UUIDString | null>(null);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { updateNotice } = useUpdateNoticeApi();
  const enqueueSnackbar = useSnackBar();

  const [pendingUpdatesToSave, setPendingUpdatesToSave] = useState<PendingUpdateBySectionId>({});
  const pendingUpdatesRef = useRef(pendingUpdatesToSave);

  useEffect(() => {
    pendingUpdatesRef.current = pendingUpdatesToSave;
  }, [pendingUpdatesToSave]);

  useEffect(() => {
    if (draftNotice) {
      publishButtonEnabledRef.current = draftNotice?.sections.some(
        (section) => !!section.header || !!section.content,
      );
    }
  }, [draftNotice]);

  const [, forceRender] = useReducer((x) => x + 1, 0);

  const stateAnchors = Object.values(privacyCenter?.anchorsByState || {});
  let draftNoticeSections = (draftNotice?.sections || []).filter(
    (section) =>
      !(
        section.anchor.startsWith("state-notice-") ||
        section.anchor == "notice-of-financial-incentive" ||
        section.anchor == "data-retention-policy" ||
        section.anchor == "colorado-biometric-identifiers-policy" ||
        stateAnchors.includes(section.anchor)
      ),
  );
  let sectionsWithPendingUpdatesApplied = draftNoticeSections
    .map((section) => {
      if (!(section.id in pendingUpdatesToSave)) {
        return section;
      }

      const pendingUpdate = pendingUpdatesToSave[section.id];

      if (pendingUpdate.isDeleted) {
        return null;
      }

      const addToNavigation =
        "addToNavigation" in pendingUpdate
          ? pendingUpdate.addToNavigation
          : section.addToNavigation;
      return {
        ...section,
        header: pendingUpdate.header ?? section.header,
        content: pendingUpdate.content ?? section.content,
        placeholder: pendingUpdate.placeholder ?? section.placeholder,
        displayOrder: pendingUpdate.displayOrder ?? section.displayOrder,
        locked: pendingUpdate.locked ?? section.locked,
        addToNavigation,
      };
    })
    .filter((section) => section !== null);

  // Add new pending sections
  Object.entries(pendingUpdatesToSave)
    .filter(([sectionId]) => !draftNoticeSections.some((dns) => dns.id === sectionId))
    .forEach(([sectionId, pendingUpdate]) => {
      if (pendingUpdate.isDeleted) {
        return;
      }

      //@ts-ignore since this is only a temporary object until the request to add the section is successful, we don't need all the fields
      sectionsWithPendingUpdatesApplied.push({
        id: sectionId,
        header: pendingUpdate.header,
        content: pendingUpdate.content,
        placeholder: pendingUpdate.placeholder,
        displayOrder: pendingUpdate.displayOrder,
        addToNavigation: pendingUpdate.addToNavigation,
        locked: pendingUpdate.locked,
      });
    });

  const sortedSections = sectionsWithPendingUpdatesApplied.sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

  const handleOutlineClick = (sectionId: string) => {
    let sectionDiv = document.getElementById(`section-${sectionId}`);
    if (sectionDiv) {
      sectionDiv.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const sendPublish = async () => {
    try {
      await updateNotice({
        organizationId: org.id,
        privacyCenterId: draftNotice.privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });
      await refreshDraftNotice();
      await allNoticesRefresh();

      setIsSaved(true);
    } catch (error) {
      console.error("Failed to publish the notice:", error);
      enqueueSnackbar("Failed to publish. Please try again.", { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const updatePrivacyCenterApi = useCallback(
    (submitValues) => Api.privacyCenter.updatePrivacyCenter(org.id, privacyCenter.id, submitValues),
    [org.id, privacyCenter.id],
  );

  const { fetch: updatePrivacyCenterValues } = useActionRequest({
    api: updatePrivacyCenterApi,
    onSuccess: privacyCenterRefresh,
  });

  const { isDateSaving, onDateChange } = usePrivacyCenterPublish(
    org.id,
    draftNotice,
    refreshDraftNotice,
  );

  const publishedAtNormalizedStr = (
    latestPublishedNoticeUpdatedOn || draftNotice?.publishedAt
  )?.replace(/-/g, "/");
  const publishedAtDate = moment(publishedAtNormalizedStr || new Date()).toDate();

  return (
    <>
      <div className="tw-boundary">
        <div className={`flex items-center justify-end`}>
          <div className="flex">
            <div className="saving-notif">
              <ProgressFooterSaveNotification saving={saving} isSaved={isSaved} />
            </div>
            {showPublishDateModal && (
              <PrivacyCenterPublishDatePickerModal
                onPublishClick={sendPublish}
                closeModal={() => setShowPublishDateModal(false)}
                // use the draft notice's publishedAt date since that gets updated as the user selects a date in the modal
                publishedAtDate={moment(draftNotice?.publishedAt || new Date()).toDate()}
                onDateChange={onDateChange}
                disabled={isDateSaving}
              />
            )}
          </div>
        </div>
      </div>
      <div style={{ display: "flex" }}>
        <div className="policy-outline-bar tw-boundary">
          <div className="space-y-4">
            <PrivacyCenterHeaderLogoSettingsItem
              org={org}
              privacyCenter={privacyCenter}
              updatePrivacyCenterValues={updatePrivacyCenterValues}
            />
            <PrivacyCenterLogoLinkUrlSettingsItem
              privacyCenter={privacyCenter}
              updatePrivacyCenterValues={updatePrivacyCenterValues}
            />
            <PrivacyCenterFaviconSettingsItem
              org={org}
              privacyCenter={privacyCenter}
              updatePrivacyCenterValues={updatePrivacyCenterValues}
            />
          </div>
          <p className="mt-[36px] mb-[22px] text-[18px] font-medium text-left leading-[28px]">
            Policy Outline
          </p>
          <DatePicker
            date={publishedAtDate}
            label="Policy Last Updated On"
            onChange={onDateChange}
            disabled={isDateSaving}
          />
          <div>
            <div style={{ margin: "10px 0" }}>
              <div className="text-neutral-600 text-[14px] font-medium leading-[18px] text-left p-[8px] rounded-t-[6px]">
                {sortedSections.map((section, sectionIdx) => {
                  if (section.addToNavigation) {
                    return (
                      <div
                        key={section.id}
                        className="cursor-pointer ml-[20px] pb-[20px]"
                        onClick={() => handleOutlineClick(section.id)}
                      >
                        {truncateWithEllipsis(section.header, 40) ||
                          `Untitled Section ${sectionIdx + 1}`}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="tw-boundary">
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <div className="flex flex-col gap-[36px] mb-[36px] justify-center items-center">
                    <div className="w-full" style={{ maxWidth: "900px" }}>
                      <ExplanatoryCard
                        topBody={
                          <div>
                            <h3 className="text-lg font-medium leading-6 text-neutral-800 mb-3">
                              What is a Privacy Policy?
                            </h3>
                            <div className="text-sm font-normal leading-5 text-gray-500 space-y-4">
                              <p>
                                A legal document that explains how an organization handles any
                                customer, client or employee information gathered in its operations.{" "}
                                <a
                                  href="https://knowledge.truevault.com/article/257-what-goes-in-a-privacy-policy"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-sm leading-5 underline text-primary-600"
                                >
                                  What Goes in a Privacy Policy?
                                </a>
                              </p>
                              <p>
                                Vendor Disclosures note: Some vendors require businesses to include
                                specific statements within their Privacy Policy.{" "}
                                <a
                                  href="https://knowledge.truevault.com/article/312-vendor-specific-privacy-policy-requirements"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-sm leading-5 underline text-primary-600"
                                >
                                  See if any of your vendors require specific language.
                                </a>
                              </p>
                            </div>
                          </div>
                        }
                        expandable={false}
                      />
                    </div>
                    <PrivacyCenterTitleSettingsItem
                      privacyCenter={privacyCenter}
                      updatePrivacyCenterValues={updatePrivacyCenterValues}
                    />
                    <PrivacyCenterWelcomeTextSettingsItem
                      privacyCenter={privacyCenter}
                      updatePrivacyCenterValues={updatePrivacyCenterValues}
                    />
                  </div>
                );
              }}
            />
          </div>
          {draftNotice && (
            <NoticeEditor
              key={draftNotice.publicId}
              privacyCenterId={privacyCenter.id}
              sections={sortedSections}
              noticeId={draftNotice.id}
              noticeType={draftNotice.type}
              pendingUpdatesRef={pendingUpdatesRef}
              pendingUpdatesToSave={pendingUpdatesToSave}
              setPendingUpdatesToSave={setPendingUpdatesToSave}
              forceRender={forceRender}
              noticePublicId={draftNotice.publicId}
              refreshNotice={refreshDraftNotice}
              organizationId={org.id}
              organizationName={org.name}
              organizationPhysicalAddress={org.messageSettings.physicalAddress}
              privacyCenterContactEmail={privacyCenter.requestEmail}
              privacyCenterOptOutUrl={privacyCenter.optOutUrl}
              saving={saving}
              setSaving={setSaving}
              isSaved={isSaved}
              setIsSaved={setIsSaved}
            />
          )}
        </div>

        <ConfirmDialog
          open={Boolean(confirmDeleteId)}
          onClose={() => setConfirmDeleteId(null)}
          onConfirm={() => {
            onDeleteSection(confirmDeleteId);
            setConfirmDeleteId(null);
          }}
          confirmTitle="Remove section?"
          contentText="Are you sure you want to remove this section? Clicking ‘yes’ will remove it from your privacy policy."
          confirmText="Yes"
          cancelText="No"
        />
      </div>
    </>
  );
};

type ChildBuiltInNoticesProps = {
  notices: BuiltInNoticeSubsection[] | undefined;
};
const ChildBuiltInNotices: React.FC<ChildBuiltInNoticesProps> = ({ notices }) => {
  if (!notices) {
    return null;
  }

  return (
    <>
      {notices.map((notice) => {
        const actions = (
          <>
            {notice.editRoute ? (
              <a href={notice.editRoute} className="text-weight-medium">
                Edit
              </a>
            ) : undefined}
          </>
        );
        return (
          <SectionCallout
            key={notice.heading}
            title={notice.heading}
            actions={actions}
            variant={notice.variant ?? notice.editRoute ? "white" : "dark"}
          >
            <Stack spacing={2}>
              <div>{notice.body}</div>

              <ChildBuiltInNotices notices={notice.childNotices} />
            </Stack>
          </SectionCallout>
        );
      })}
    </>
  );
};

type BuiltInNoticeSlug =
  | "collect"
  | "disclose"
  | "ccpa"
  | "vcdpa"
  | "cpa"
  | "ctdpa"
  | "utcpa"
  | "tdpsa"
  | "mcdpa"
  | "ocpa"
  | "fdbr"
  | "icdpa"
  | "dpdpa"
  | "nhpa"
  | "ndpa"
  | "njdpa"
  | "npicica"
  | "modpa"
  | "financial"
  | "gdpr"
  | "pipeda";

export type BuiltInNoticeSection = {
  heading: string;
  slug: BuiltInNoticeSlug;
  displayOrder: number;
  custom: false;

  route?: string;

  childNotices?: BuiltInNoticeSubsection[];
};

type BuiltInNoticeSubsection = {
  heading?: string;
  variant?: "dark" | "white";

  body: React.ReactNode;

  editRoute?: string;

  childNotices?: BuiltInNoticeSubsection[];
};

const useNoticeEditorMetadata = (
  organizationId: OrganizationPublicId,
  privacyCenterId: PrivacyCenterPublicId,
): [NoticeEditorMetadataResponse, NetworkRequestState] => {
  const apiRequest = useCallback(
    () => Api.notice.getNoticeEditorMetadata(organizationId, privacyCenterId),
    [organizationId, privacyCenterId],
  );

  const { result: noticeEditorMetadata, request } = useDataRequest({
    queryKey: ["privacyCenter", organizationId, privacyCenterId, "noticeEditorMetadata"],
    api: apiRequest,
    dependencies: [organizationId, privacyCenterId],
  });

  return [noticeEditorMetadata, request];
};

interface NoticeEditorProps extends React.FormHTMLAttributes<HTMLFormElement> {
  privacyCenterId: string;
  noticeId: string;
  noticePublicId: PrivacyCenterNoticePublicId;
  noticeType: string;
  sections: PrivacyCenterNoticeSectionDto[];
  refreshNotice: () => Promise<void>;
  pendingUpdatesRef: React.MutableRefObject<PendingUpdateBySectionId>;
  pendingUpdatesToSave: PendingUpdateBySectionId;
  setPendingUpdatesToSave: React.Dispatch<React.SetStateAction<PendingUpdateBySectionId>>;
  forceRender: () => void;
  organizationId: OrganizationPublicId;
  organizationName: string;
  organizationPhysicalAddress?: string;
  privacyCenterContactEmail?: string;
  privacyCenterOptOutUrl?: string;
  onSubmit?: (values: any) => void;
  onBlur?: (values: any) => void;
  saving?: boolean;
  setSaving?: React.Dispatch<React.SetStateAction<boolean>>;
  isSaved?: boolean;
  setIsSaved?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NoticeEditor = ({
  sections,
  privacyCenterId,
  noticeId,
  noticePublicId,
  noticeType,
  pendingUpdatesRef,
  pendingUpdatesToSave,
  setPendingUpdatesToSave,
  onSubmit,
  organizationId,
  organizationName,
  organizationPhysicalAddress,
  privacyCenterContactEmail,
  privacyCenterOptOutUrl,
  refreshNotice,
  forceRender,
  setIsSaved,
  setSaving,
}: NoticeEditorProps) => {
  const { updateNotice } = useUpdateNoticeApi();
  const { deleteSection } = useDeleteSectionApi();
  const { addSection } = useCreateSectionApi({
    onSuccess: async (_) => {
      await refreshNotice();
    },
  });
  const history = useHistory();

  const { answers: developerInstructionsAnswers, setAnswer: setDeveloperInstructionsAnswer } =
    useSurvey(organizationId, DEVELOPER_INSTRUCTIONS_SURVEY_NAME);

  const [noticeEditorMetadata] = useNoticeEditorMetadata(organizationId, privacyCenterId);

  // Disclaimer modal that appears when unlocking the GDPR section
  const [showGdprUnlockDisclaimerModal, setShowGdprUnlockDisclaimerModal] = useState(false);
  const [gdprSectionIdToUnlock, setGdprSectionIdToUnlock] = useState<string | null>(null);

  // Disclaimer modal that appears when unlocking the PIPEDA section
  const [showPipedaUnlockDisclaimerModal, setShowPipedaUnlockDisclaimerModal] = useState(false);
  const [pipedaSectionIdToUnlock, setPipedaSectionIdToUnlock] = useState<string | null>(null);

  const debouncedSave = useCallback(
    _.debounce(async (pendingUpdates: PendingUpdateBySectionId) => {
      setSaving(true);

      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = "";
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      let unblock = history.block("You have unsaved changes. Are you sure you want to leave?");

      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: noticePublicId,
        updateNoticeBody: {
          id: noticeId,
          sections: Array.from(Object.entries(pendingUpdates))
            // Even though the parent component should be filtering out deleted sections, sometimes we still end up with deleted sections here, so filter them out again here as well
            .filter(([, sectionUpdate]) => !sectionUpdate.isDeleted)
            .map(([sectionId, sectionUpdate]) => ({ id: sectionId, ...sectionUpdate })),
        },
      };

      await updateNotice(updateNoticeBody);

      setSaving(false);
      setIsSaved(true);
      setTimeout(() => {
        setIsSaved(false);
      }, 2000);

      window.removeEventListener("beforeunload", handleBeforeUnload);
      unblock();

      await refreshNotice();
    }, 750),
    [],
  );

  const handleSectionChange = useCallback(
    (sectionId: string, updatedContent: { header?: string; content?: string }) => {
      const pendingUpdatesFromRef = pendingUpdatesRef.current;
      const newPendingUpdates = _.cloneDeep(pendingUpdatesFromRef);
      if (sectionId in newPendingUpdates) {
        newPendingUpdates[sectionId] = { ...newPendingUpdates[sectionId], ...updatedContent };
      } else {
        newPendingUpdates[sectionId] = { ...updatedContent };
      }
      setPendingUpdatesToSave(newPendingUpdates);
      debouncedSave(newPendingUpdates);
    },
    [pendingUpdatesToSave, setPendingUpdatesToSave],
  );

  const handleUnlock = useCallback(
    (sectionId: string) => {
      const pendingUpdatesFromRef = pendingUpdatesRef.current;
      const newPendingUpdates = _.cloneDeep(pendingUpdatesFromRef);
      if (sectionId in newPendingUpdates) {
        newPendingUpdates[sectionId].locked = false;
      } else {
        newPendingUpdates[sectionId] = { locked: false };
      }
      setPendingUpdatesToSave(newPendingUpdates);
      debouncedSave(newPendingUpdates);
    },
    [pendingUpdatesToSave, setPendingUpdatesToSave],
  );

  const handleDelete = useCallback(
    async (sectionId) => {
      const pendingUpdatesFromRef = pendingUpdatesRef.current;
      const newPendingUpdates = _.cloneDeep(pendingUpdatesFromRef);
      if (sectionId in newPendingUpdates) {
        newPendingUpdates[sectionId].isDeleted = true;
      } else {
        newPendingUpdates[sectionId] = { isDeleted: true };
      }
      setPendingUpdatesToSave(newPendingUpdates);
      await deleteSection({
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: noticePublicId,
        sectionId: sectionId, // Use the passed sectionId here
      });
      await refreshNotice();
    },
    [pendingUpdatesToSave, setPendingUpdatesToSave],
  );

  const handleShowInNavigationCheckboxToggle = useCallback(
    (sectionId: string, newVal: boolean) => {
      const pendingUpdatesFromRef = pendingUpdatesRef.current;
      const newPendingUpdates = _.cloneDeep(pendingUpdatesFromRef);
      if (sectionId in newPendingUpdates) {
        newPendingUpdates[sectionId].addToNavigation = newVal;
      } else {
        newPendingUpdates[sectionId] = { addToNavigation: newVal };
      }
      setPendingUpdatesToSave(newPendingUpdates);
      debouncedSave(newPendingUpdates);
    },
    [pendingUpdatesToSave, setPendingUpdatesToSave],
  );

  const handleMoveSectionUp = (sectionId) => {
    const currentSectionIndex = sections.findIndex((section) => section.id === sectionId);
    const sectionToMove = sections[currentSectionIndex];

    if (currentSectionIndex <= 0) return;

    const newPendingUpdates = _.cloneDeep(pendingUpdatesToSave);

    let prevSection = sections[currentSectionIndex - 1];

    if (prevSection.id in newPendingUpdates) {
      newPendingUpdates[prevSection.id].displayOrder = sectionToMove.displayOrder;
    } else {
      newPendingUpdates[prevSection.id] = { displayOrder: sectionToMove.displayOrder };
    }

    if (sectionToMove.id in newPendingUpdates) {
      newPendingUpdates[sectionToMove.id].displayOrder = prevSection.displayOrder;
    } else {
      newPendingUpdates[sectionToMove.id] = { displayOrder: prevSection.displayOrder };
    }

    setPendingUpdatesToSave(newPendingUpdates);
    debouncedSave(newPendingUpdates);

    setTimeout(() => {
      let sectionDiv = document.getElementById(`section-${sectionId}`);
      if (sectionDiv) {
        sectionDiv.scrollIntoView({
          // @ts-ignore Our version of TS doesn't have the instant option https://github.com/Microsoft/TypeScript/issues/28755
          behavior: "instant",
          block: "center",
        });
      }
    }, 100);
  };

  const handleMoveSectionDown = (sectionId) => {
    const currentSectionIndex = sections.findIndex((section) => section.id === sectionId);
    const sectionToMove = sections[currentSectionIndex];

    if (currentSectionIndex >= sections.length - 1) return;

    let nextSection = sections[currentSectionIndex + 1];

    if (nextSection.locked) return;

    const newPendingUpdates = _.cloneDeep(pendingUpdatesToSave);

    if (nextSection.id in newPendingUpdates) {
      newPendingUpdates[nextSection.id].displayOrder = sectionToMove.displayOrder;
    } else {
      newPendingUpdates[nextSection.id] = { displayOrder: sectionToMove.displayOrder };
    }

    if (sectionToMove.id in newPendingUpdates) {
      newPendingUpdates[sectionToMove.id].displayOrder = nextSection.displayOrder;
    } else {
      newPendingUpdates[sectionToMove.id] = { displayOrder: nextSection.displayOrder };
    }

    setPendingUpdatesToSave(newPendingUpdates);
    debouncedSave(newPendingUpdates);

    setTimeout(() => {
      let sectionDiv = document.getElementById(`section-${sectionId}`);
      if (sectionDiv) {
        sectionDiv.scrollIntoView({
          // @ts-ignore Our version of TS doesn't have the instant option https://github.com/Microsoft/TypeScript/issues/28755
          behavior: "instant",
          block: "center",
        });
      }
    }, 100);
  };

  const isMinimalState = sections.filter((section) => section.custom).length == 1;

  const localSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (onSubmit) {
      onSubmit(e);
    }
  };

  const handleAddSectionClick = useCallback(
    async (addSectionAction: AddSectionButtonAction) => {
      let newDisplayOrder =
        sections
          .filter((section) => !section.locked)
          .reduce(
            (highestIndexYet, currentSection) =>
              Math.max(highestIndexYet, currentSection.displayOrder),
            0,
          ) + 1;

      let newSectionBodies = [];

      if (addSectionAction === "Blank") {
        newSectionBodies.push({
          id: crypto.randomUUID(),
          displayOrder: newDisplayOrder,
          header: "",
          placeholder:
            noticeType === "PRIVACY_POLICY"
              ? "Copy and paste a section of your privacy policy here, or add a customized section below."
              : "", // todo: pol-4219 Change section body placeholder text depending on parent notice type
        });
      } else if (addSectionAction === "ADD_ALL_CORE_SECTIONS") {
        for (const sectionType of CoreSectionTemplates) {
          newSectionBodies.push({
            id: crypto.randomUUID(),
            displayOrder: newDisplayOrder,
            header: SectionTemplateTypeToSectionHeader[sectionType],
            content: getSectionContentsFromTemplate(
              sectionType,
              noticeEditorMetadata,
              organizationName,
              organizationPhysicalAddress,
              privacyCenterContactEmail,
              privacyCenterOptOutUrl,
            ),
            placeholder: undefined,
          });
          newDisplayOrder++;
        }
      } else {
        newSectionBodies.push({
          id: crypto.randomUUID(),
          displayOrder: newDisplayOrder,
          header: SectionTemplateTypeToSectionHeader[addSectionAction],
          content: getSectionContentsFromTemplate(
            addSectionAction,
            noticeEditorMetadata,
            organizationName,
            organizationPhysicalAddress,
            privacyCenterContactEmail,
            privacyCenterOptOutUrl,
          ),
          placeholder: undefined,
        });
      }

      const createSectionRequest: CreateSectionApiArgs = {
        organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: noticePublicId,
        createSectionBody: {
          sections: newSectionBodies,
        },
      };

      const pendingUpdatesFromRef = pendingUpdatesRef.current;
      const newPendingUpdates = _.cloneDeep(pendingUpdatesFromRef);
      newSectionBodies.forEach((newSectionBody) => {
        newPendingUpdates[newSectionBody.id] = {
          addToNavigation: true,
          content: newSectionBody.content || "",
          placeholder: newSectionBody.placeholder || "",
          displayOrder: newSectionBody.displayOrder,
          header: newSectionBody.header || "",
          locked: false,
          isDeleted: false,
        };
      });
      setPendingUpdatesToSave(newPendingUpdates);

      forceRender();

      await addSection(createSectionRequest);
      await refreshNotice();
    },
    [pendingUpdatesToSave, setPendingUpdatesToSave],
  );

  const isGdprSectionLocked = sections.some(
    (s) => (s.anchor == "eea-uk-privacy-notice" || s.anchor == "eeauk-gdpr") && s.locked,
  );
  const isPipedaSectionLocked = sections.some(
    (s) => s.anchor == "ca-pipeda-privacy-notice" && s.locked,
  );
  const addSectionButton = (
    <div className="tw-boundary" style={{ marginBottom: "55px" }}>
      <AddNewSectionButton
        addSection={(type: AddSectionButtonAction) => handleAddSectionClick(type)}
        shouldShowConfirmationForAddingAllSections={
          sections.filter((section) => !section.locked).length > 0
        }
        showGdprSectionTemplate={
          noticeEditorMetadata?.gdprTemplate?.length > 0 && !isGdprSectionLocked
        }
        showPipedaSectionTemplate={
          noticeEditorMetadata?.pipedaTemplate?.length > 0 && !isPipedaSectionLocked
        }
        showShopifySNITemplate={noticeEditorMetadata?.hasShopifySNIDataRecipient}
      />
    </div>
  );

  const sectionEditorForSectionId = (
    section: PrivacyCenterNoticeSectionDto,
    moveUpDisabled: boolean,
    moveDownDisabled: boolean,
    hideSideControls: boolean,
  ) => {
    const header =
      section.id in pendingUpdatesToSave && "header" in pendingUpdatesToSave[section.id]
        ? pendingUpdatesToSave[section.id].header
        : section.header;
    const content =
      section.id in pendingUpdatesToSave && "content" in pendingUpdatesToSave[section.id]
        ? pendingUpdatesToSave[section.id].content
        : section.content;
    return (
      <div
        id={`section-${section.id}`}
        className="individual-section-wrapper"
        style={{ width: "100%" }}
        key={section.id}
      >
        <SectionEditor
          addToNavigation={section.addToNavigation}
          key={section.id}
          sectionId={section.id}
          header={header}
          content={content}
          locked={section.locked}
          placeholder={section.placeholder}
          onPositionUp={() => handleMoveSectionUp(section.id)}
          onPositionDown={() => handleMoveSectionDown(section.id)}
          moveDisabled={isMinimalState}
          moveUpDisabled={moveUpDisabled}
          moveDownDisabled={moveDownDisabled}
          deleteDisabled={sections.length <= 1}
          hideSideControls={hideSideControls}
          onShowInNavigationCheckboxToggle={() => {
            handleShowInNavigationCheckboxToggle(section.id, !section.addToNavigation);
          }}
          onDelete={() => {
            handleDelete(section.id);
          }}
          onInputChange={(updatedContent) => {
            handleSectionChange(section.id, updatedContent);
          }}
          onUnlock={() => {
            if (
              section.anchor == "eea-uk-privacy-notice" ||
              section.anchor == "eeauk-gdpr" ||
              section.anchor == "eeaukch-gdpr"
            ) {
              setShowGdprUnlockDisclaimerModal(true);
              setGdprSectionIdToUnlock(section.id);
            } else if (
              section.anchor == "ca-pipeda-privacy-notice" ||
              section.anchor == "canada-pipeda"
            ) {
              setShowPipedaUnlockDisclaimerModal(true);
              setPipedaSectionIdToUnlock(section.id);
            } else {
              handleUnlock(section.id);
            }
          }}
          enablePrivacyCenterToolbar={true}
          availablePrivacyCenterEmbeddedAssets={
            new Map(Object.entries(noticeEditorMetadata?.availableEmbeddedAssets || {}))
          }
        />
      </div>
    );
  };

  const unlockedSections = sections.filter((section) => !section.locked);
  const lockedSections = sections.filter((section) => section.locked);

  const didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt =
    developerInstructionsAnswers &&
    developerInstructionsAnswers[
      DevInstructionsStartWithTruevaultPrivacyPolicyTemplatePromptSlug
    ] === "true";

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", width: "900px" }}>
        {!didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt && (
          <StartWithTruevaultPrivacyPolicyTemplatePrompt
            onClick={(action) => {
              if (action === "START_WITH_TEMPLATE") {
                handleAddSectionClick("ADD_ALL_CORE_SECTIONS");
              } else if (action === "START_FROM_SCRATCH") {
                handleAddSectionClick("Blank");
              }

              setDeveloperInstructionsAnswer(
                DevInstructionsStartWithTruevaultPrivacyPolicyTemplatePromptSlug,
                "true",
              );
            }}
          />
        )}
        <Form
          onSubmit={localSubmit}
          render={({ handleSubmit }) => (
            <div>
              <form className="flex flex-col align-items-center" onSubmit={handleSubmit}>
                {unlockedSections?.map((section, idx) => {
                  const sectionEditorDiv = sectionEditorForSectionId(
                    section,
                    /* moveUpDisabled = */ idx == 0,
                    /* moveDownDisabled = */ idx == unlockedSections.length - 1,
                    /* hideSideControls = */ !didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt,
                  );

                  if (idx == unlockedSections.length - 1) {
                    return (
                      <div key={section.id} className="flex flex-col align-items-center">
                        {sectionEditorDiv}
                        <div className="dashed-line"></div>
                        {addSectionButton}
                      </div>
                    );
                  } else {
                    return (
                      <div key={section.id} className="flex flex-col align-items-center">
                        {sectionEditorDiv}
                        <div className="dashed-line"></div>
                      </div>
                    );
                  }
                })}
                {unlockedSections.length == 0 &&
                  didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt &&
                  addSectionButton}
                {lockedSections.map((section) => {
                  const sectionEditorDiv = sectionEditorForSectionId(
                    section,
                    /* moveUpDisabled = */ false,
                    /* moveDownDisabled = */ false,
                    /* hideSideControls = */ !didAnswerStartWithTruevaultPrivacyPolicyTemplatePrompt,
                  );
                  return (
                    <div key={section.id} className="flex flex-col align-items-center">
                      {sectionEditorDiv}
                    </div>
                  );
                })}
              </form>
            </div>
          )}
        />
      </div>
      {showGdprUnlockDisclaimerModal && (
        <Modal
          title="You are about to alter the EEA/UK/CH GDPR Notice"
          alignText="center"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            handleUnlock(gdprSectionIdToUnlock);
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          showOverlay
          onClose={() => {
            setShowGdprUnlockDisclaimerModal(false);
            setGdprSectionIdToUnlock(null);
          }}
          widthButton="full"
          alignButton="right"
        >
          <p className="text-center text-gray-500">
            Our notice templates have been thoroughly researched and reviewed for compliance and get
            regularly updated with new regulations and changes to your Data Map.
          </p>
          <p className="text-center text-gray-500" style={{ marginTop: "35px" }}>
            By unlocking and changing the notice you assume responsibility for its content. You can
            add the GDPR Notice template at any time.
          </p>
        </Modal>
      )}
      {showPipedaUnlockDisclaimerModal && (
        <Modal
          title="You are about to alter the Canada PIPEDA Notice"
          alignText="center"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => {
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          secondButton="I Understand"
          secondButtonColor="warning"
          onSecondButtonClick={() => {
            handleUnlock(pipedaSectionIdToUnlock);
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          showOverlay
          onClose={() => {
            setShowPipedaUnlockDisclaimerModal(false);
            setPipedaSectionIdToUnlock(null);
          }}
          widthButton="full"
          alignButton="right"
        >
          <p className="text-center text-gray-500">
            Our notice templates have been thoroughly researched and reviewed for compliance and get
            regularly updated with new regulations and changes to your Data Map.
          </p>
          <p className="text-center text-gray-500" style={{ marginTop: "35px" }}>
            By unlocking and changing the notice you assume responsibility for its content. You can
            restore the PIPEDA Notice template at any time.
          </p>
        </Modal>
      )}
    </>
  );
};

export type AddSectionButtonAction = NewSectionType | "ADD_ALL_CORE_SECTIONS";
const AddNewSectionButton = ({
  addSection,
  shouldShowConfirmationForAddingAllSections,
  showGdprSectionTemplate,
  showPipedaSectionTemplate,
  showShopifySNITemplate,
}: {
  addSection: (addSectionAction: AddSectionButtonAction) => void;
  shouldShowConfirmationForAddingAllSections: boolean;
  showGdprSectionTemplate: boolean;
  showPipedaSectionTemplate: boolean;
  showShopifySNITemplate: boolean;
}) => {
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useLocalStorage(
    "hasSeenSectionTemplateDisclaimer",
    "false",
  );

  const [disclaimerModalVisible, setDisclaimerModalVisible] = useState(false);
  const [savedAction, setSavedAction] = useState<AddSectionButtonAction | null>(null);

  const [showAddAllSectionsConfirmationModal, setShowAddAllSectionsConfirmationModal] =
    useState(false);

  const handleMenuItemClick = (addSectionAction: AddSectionButtonAction) => {
    if (hasSeenDisclaimer == "true") {
      if (
        addSectionAction === "ADD_ALL_CORE_SECTIONS" &&
        shouldShowConfirmationForAddingAllSections
      ) {
        setShowAddAllSectionsConfirmationModal(true);
      } else {
        addSection(addSectionAction);
      }
    } else {
      setSavedAction(addSectionAction);
      setDisclaimerModalVisible(true);
      setHasSeenDisclaimer("true");
    }
  };

  const closeDisclaimerModal = () => {
    setDisclaimerModalVisible(false);
    setSavedAction(null);
  };

  let dropdownOptions: NewSectionDropdownTemplate[] = [
    { label: "Introduction", sectionType: "Introduction", templateGroup: "Core" },
    {
      label: "Changes to this Privacy Policy",
      sectionType: "Changes_To_Privacy_Policy",
      templateGroup: "Core",
    },
    {
      label: "Information collection",
      sectionType: "Information_Collection",
      templateGroup: "Core",
    },
    {
      label: "Why we process your information",
      sectionType: "Why_We_Process_Your_Information",
      templateGroup: "Core",
    },
    {
      label: "How we disclose your personal information",
      sectionType: "How_We_Disclose_Your_Personal_Information",
      templateGroup: "Core",
    },
  ];

  if (showShopifySNITemplate) {
    dropdownOptions.push({
      label: "Shopify Network Intelligence",
      sectionType: "Shopify_SNI",
      templateGroup: "Core",
    });
  }

  dropdownOptions.push(
    {
      label: "Cookies & similar technologies",
      sectionType: "Cookies_Similar_Technologies",
      templateGroup: "Core",
    },
    {
      label: "California Shine the Light Disclosure",
      sectionType: "California_Shine_Light_Disclosure",
      templateGroup: "Core",
    },
  );
  if (showGdprSectionTemplate) {
    dropdownOptions.push({
      label: "EEA/UK/CH (GDPR)",
      sectionType: "EEA_UK_GDPR",
      templateGroup: "International",
    });
  }
  if (showPipedaSectionTemplate) {
    dropdownOptions.push({
      label: "Canada (Pipeda)",
      sectionType: "CANADA_PIPEDA",
      templateGroup: "International",
    });
  }
  dropdownOptions.push({ label: "Contact Us", sectionType: "Contact_Us", templateGroup: "Core" });

  return (
    <>
      <AddNewSectionDropdown
        buttonText="Add New Section/Template"
        dropdownOptions={dropdownOptions}
        onClickHandler={handleMenuItemClick}
      />
      {disclaimerModalVisible && (
        <DisclaimerModal
          onAcceptDisclosure={() => {
            setDisclaimerModalVisible(false);
            setSavedAction(null);
            if (
              savedAction === "ADD_ALL_CORE_SECTIONS" &&
              shouldShowConfirmationForAddingAllSections
            ) {
              setShowAddAllSectionsConfirmationModal(true);
            } else {
              addSection(savedAction);
            }
          }}
          onClose={closeDisclaimerModal}
        />
      )}
      {showAddAllSectionsConfirmationModal && (
        <Modal
          title=""
          alignText="left"
          firstButton="Cancel"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setShowAddAllSectionsConfirmationModal(false)}
          secondButton="Continue"
          secondButtonColor="primary"
          onSecondButtonClick={() => {
            setShowAddAllSectionsConfirmationModal(false);
            addSection("ADD_ALL_CORE_SECTIONS");
          }}
          showOverlay
          onClose={() => setShowAddAllSectionsConfirmationModal(false)}
          alignButton="right"
        >
          <p>
            You are about to add eight (8) templated sections to your Privacy Policy. Do you want to
            continue?
          </p>
        </Modal>
      )}
    </>
  );
};

export function DisclaimerModal({
  onAcceptDisclosure,
  onClose,
}: {
  onAcceptDisclosure: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Disclaimer regarding TrueVault Policy Templates"
      alignText="left"
      firstButton="Cancel"
      firstButtonColor="tertiary"
      onFirstButtonClick={onClose}
      secondButton="Sounds Good!"
      secondButtonColor="primary"
      onSecondButtonClick={onAcceptDisclosure}
      showOverlay
      onClose={onClose}
      alignButton="right"
    >
      <p className="text-left">
        Your business may be governed by other laws or agreements (such as vendor contracts) that
        are outside the scope of our templates.
      </p>
      <p className="text-left mt-2">
        Example text is for informational purposes and does not constitute legal services. If you
        have questions about what must be included in your privacy policy, please consult an
        attorney.
      </p>
    </Modal>
  );
}
